import { useTranslation } from "react-i18next";
import { Grid, Typography, Box } from "@mui/material";
import moment from "moment";
import generateCloudinaryLink from 'src/_utils/generateCloudinaryLink';
import { get, isNumber } from "lodash";
import { useSelector } from "react-redux";
import { IRONER } from "src/constants";
import { useMemo } from "react";

const AWB = ({ index, order, style, ...props }) => {

    const { t } = useTranslation();
    const { profile } = useSelector((state) => state.entities.profile);

    const AWBTypography = ({ children, ...props }) => <Typography letterSpacing="normal" {...props} >{children}</Typography>

    const {
        orderNumber,
        createdAt,
        barCode,
        zone,
        amountPayable,
        user,
        merchant,
        shippingCompany,
        deliveryType,
        packagesCount
    } = order;

    const HEIGHT = 720; // 1168, {230, 104, 104, 640, 90}
    const WIDTH = HEIGHT * 100 / 150; // 768
    const BORDER_WIDTH = 1;
    const gridItemStyle = {
        borderColor: "#000000",
        borderStyle: 'solid',
        borderWidth: `${BORDER_WIDTH}px`,
        flex: 1,
        display: "flex",
        flexDirection: "row",
    }

    const getLogo = () => {
        if (deliveryType === "EXTERNAL_DELIVERY") {
            return get(shippingCompany, 'logo')
        } else if (deliveryType === "INTERNAL_DELIVERY") {
            return get(merchant, 'logo')
        } else {
            return get(shippingCompany, 'logo') ?? get(merchant, 'logo') ?? profile?.merchant?.logo ?? { logo: null }
        }
    }

    const showProduct = get(order?.merchant, 'orderItemsInAWB') || get(order?.shippingCompany, 'orderItemsInAWB') || profile?.merchant?.orderItemsInAWB

    const orderItems = useMemo(() => {
        if (!!order?.orderItems) {
            let items = [];
            order.orderItems.forEach(item => {
                for (let i = 0; i < Number(item?.amount); i++) {
                    items.push(item);
                }
            });
            return items;
        }
        return order?.orderItems
    }, [order])

    const orderItem = useMemo(() => {
        if (!!orderItems[index]) {
            return orderItems[index]
        } else {
            return orderItems[orderItems?.length - 1]
        }
    }, [orderItems])

    const dimentions = useMemo(() => {
        if (showProduct) {
            return {
                leftWidth: 12 * 720 / 960,
                rightWidth: 12 * 240 / 960,
                fontSize: "h6",
                barcodeSx: { transform: `rotate(270deg)`, width: 0.60 * HEIGHT * 640 / 1168, aspectRatio: 2.35 / 1, },
                mainSectionHeight: 429
            }
        } else {
            return {
                leftWidth: 12 * 600 / 960,
                rightWidth: 12 * 360 / 960,
                fontSize: "h4",
                barcodeSx: { width: 0.65 * HEIGHT * 640 / 1168, aspectRatio: 2.35 / 1, transform: `rotate(270deg)` },
                mainSectionHeight: 536
            }
        }
    }, [showProduct])

    return (
        <Box style={{ ...style, direction: 'ltr' }} sx={{ width: `${WIDTH}px`, height: `${HEIGHT}px`, borderColor: "#000000", borderStyle: 'solid', borderRadius: 0, borderWidth: `${BORDER_WIDTH}px`, my: 2 }} {...props}>
            <Grid container>
                {/** Merchant Logo */}
                <Grid item xs={12 * 400 / 960}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 230 / 1168}px`, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Box
                            component="img"
                            sx={{ maxHeight: "80%", maxWidth: "80%" }}
                            alt={"Barcode"}
                            src={getLogo() ? generateCloudinaryLink(getLogo()) : `/mapit-logo.png`}
                        />
                    </Box>
                </Grid>
                {/** Barcode */}
                <Grid item xs={12 * 560 / 960}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 230 / 1168}px`, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {!!barCode.image ?
                            <Box
                                component="img"
                                sx={{ maxHeight: "80%", maxWidth: "80%", aspectRatio: 2.35 / 1 }}
                                alt={"Barcode"}
                                src={generateCloudinaryLink(barCode.image)}
                            />
                            :
                            <Typography>{barCode.text}</Typography>
                        }
                    </Box>
                </Grid>
                {/** Order No. and Date */}
                <Grid item xs={12 * 400 / 960}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 104 / 1168}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', px: 2, }}>
                        <AWBTypography variant='caption' color='text.dark'> {t("awb.orderNo")} </AWBTypography>
                        <AWBTypography variant='h4' color='text.dark' mt={-0.7}>
                            {IRONER?.includes(merchant?.id) ? get(order, 'merchantOrderNumber') : orderNumber}
                        </AWBTypography>
                        <AWBTypography variant='caption' color='text.dark' mt={-0.7}> {t("awb.date", { date: moment(createdAt).locale("en").format("DD/MM/YYYY") })} </AWBTypography>
                    </Box>
                </Grid>
                {/** Zone */}
                <Grid item xs={12 * 560 / 960}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 104 / 1168}px`, backgroundColor: "#000000", display: "flex", alignItems: 'center', justifyContent: 'center' }}>
                        <AWBTypography variant='h1' color='text.white'> {get(zone, 'name') || get(user, 'address.city')} </AWBTypography>
                    </Box>
                </Grid>
                {/** Submerchant */}
                <Grid item xs={dimentions?.leftWidth}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 104 / 1168}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', px: 2, }}>
                        <AWBTypography variant='body1' color='text.dark'> {t("awb.from")} </AWBTypography>
                        <AWBTypography variant='h4' color='text.dark' mt={-0.7}> {get(merchant, 'companyName') || get(shippingCompany, 'companyName')} </AWBTypography>
                    </Box>
                </Grid>
                {/** COD */}
                <Grid item xs={dimentions?.rightWidth}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 104 / 1168}px`, display: "flex", flexDirection: 'column', alignItems: 'center', justifyContent: 'center', }}>
                        <AWBTypography variant='body1' color='text.dark'> COD </AWBTypography>
                        <AWBTypography variant={dimentions.fontSize} color='text.dark' mt={-0.7} lineHeight={1.1} textAlign="center"> {t("awb.cod", { cod: amountPayable.toFixed(2) })} </AWBTypography>
                    </Box>
                </Grid>
                {/** Merchant orderIssueNumber*/}
                <Grid item xs={dimentions?.leftWidth}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 104 / 1168}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', px: 2, }}>
                        <AWBTypography variant='body1' color='text.dark'> {t("awb.merchantOrderNumber")} </AWBTypography>
                        <AWBTypography variant='h6' color='text.dark' sx={{ wordBreak: 'break-all', mt: -0.7, ...(order?.merchantOrderNumber?.length > 22 && { fontSize: 16, lineHeight: 0.9, mt: -0.5 }) }}> {get(order, 'merchantOrderNumber') || "----"} </AWBTypography>
                    </Box>
                </Grid>
                {/** Packages count */}
                <Grid item xs={dimentions?.rightWidth}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 104 / 1168}px`, display: "flex", flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <AWBTypography variant='body1' color='text.dark'> {t("awb.packages")} </AWBTypography>
                        <AWBTypography variant={dimentions?.fontSize} color='text.dark' mt={-0.7}>{isNumber(index) ? `(${index + 1}/${packagesCount})` : packagesCount}</AWBTypography>
                    </Box>
                </Grid>
                {/** Customer */}
                <Grid item xs={dimentions?.leftWidth}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * dimentions.mainSectionHeight / 1168}px`, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', py: 1 }}>
                        <Box px={2}>
                            <AWBTypography variant='body1' color='text.dark'> {t("awb.to")} </AWBTypography>
                            {get(user, 'name') && <AWBTypography variant='h6' color='text.dark' mt={-0.7}> {user.name} </AWBTypography>}
                            {get(user, 'phoneCode') && get(user, 'phone') && <AWBTypography variant='h6' color='text.dark' mt={-0.7}> {user.phoneCode + user.phone} </AWBTypography>}

                            <Box sx={{ my: 0.5, width: '100%', borderBottomColor: "#000000", borderBottomStyle: 'dashed', borderBottomWidth: `${2 * BORDER_WIDTH}px`, }} />
                            {
                                !!get(user, 'address.area') &&
                                <AWBTypography variant='body2' color='text.dark' mt={0}>
                                    {t("awb.area", { area: user?.address?.area })}
                                </AWBTypography>
                            }
                            {get(user, 'address.street') &&
                                <AWBTypography variant='body2' color='text.dark' mt={0}>
                                    {t("awb.street", { street: user.address.street })}
                                </AWBTypography>
                            }
                            {
                                !!get(user, 'address.houseNumber') &&
                                <Box sx={{ display: 'flex', flexDirection: 'row', mt: -0.5 }}>
                                    <AWBTypography variant='body2' color='text.dark'> {t("awb.houseNumber", { houseNumber: user.address.houseNumber })} </AWBTypography>
                                </Box>
                            }

                            {
                                !!get(user, 'address.floor') &&
                                <Box sx={{ display: 'flex', flexDirection: 'row', mt: -0.5 }}>
                                    <AWBTypography variant='body2' color='text.dark'> {t("awb.floor", { floor: user.address.floor })} </AWBTypography>
                                </Box>
                            }

                            {
                                !!get(user, 'address.flat') &&
                                <Box sx={{ display: 'flex', flexDirection: 'row', mt: -0.5 }}>
                                    <AWBTypography variant='body2' color='text.dark'> {t("awb.flat", { flat: user.address.flat })} </AWBTypography>
                                </Box>
                            }

                            {get(user, 'address.city') && <AWBTypography variant='body2' color='text.dark' mt={-0.5}> {t("awb.city", { city: user?.address?.city })} </AWBTypography>}

                            {get(user, 'address.landmark') &&
                                <>
                                    <Box sx={{ my: 0.5, width: '100%', borderBottomColor: "#000000", borderBottomStyle: 'dashed', borderBottomWidth: `${2 * BORDER_WIDTH}px`, }} />
                                    <AWBTypography variant='body2' color='text.dark' mt={0}>
                                        {user.address.landmark.length > 250 ? user.address.landmark.substring(0, 250) + "..." : user.address.landmark}
                                    </AWBTypography>
                                </>
                            }
                        </Box>

                    </Box>
                </Grid>
                {/** Packages + Barcode vertical */}
                <Grid item xs={dimentions?.rightWidth}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * dimentions.mainSectionHeight / 1168}px`, flexDirection: 'column' }}>
                        {/* <Box sx={{
                            height: `${HEIGHT * 104 / 1168}px`,
                            borderBottomColor: "#000000",
                            borderBottomStyle: 'solid',
                            borderBottomWidth: `${2 * BORDER_WIDTH}px`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <AWBTypography variant='body1' color='text.dark'> {t("awb.packages")} </AWBTypography>
                            <AWBTypography variant='h4' color='text.dark' mt={-0.7}>{packagesCount}</AWBTypography>
                        </Box> */}
                        <Box sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {!!barCode.image ?
                                <Box
                                    component="img"
                                    sx={{ ...dimentions.barcodeSx }}
                                    alt={"Barcode"}
                                    src={generateCloudinaryLink(barCode.image)}
                                />
                                :
                                <Typography>{barCode.text}</Typography>
                            }
                        </Box>
                    </Box>
                </Grid>
                {
                    (showProduct && !!order?.orderItems?.length) &&
                    <Grid item xs={12}>
                        <Box
                            px={2}
                            pt={0.5}
                            height={`${HEIGHT * 110 / 1168}px`}
                            sx={{
                                borderColor: "#000000",
                                borderStyle: 'solid',
                                borderWidth: `${BORDER_WIDTH}px`,
                            }}>
                            <Box display="flex" alignItems="center">
                                <AWBTypography fontSize="14px" color='text.dark' style={{ marginRight: 3 }} sx={{ alignSelf: "start", pt: 0.2 }}> {"Prod. "} </AWBTypography>
                                <AWBTypography fontSize="17px" fontWeight="bold" color='text.dark' lineHeight={1}>
                                    {orderItem?.item}
                                </AWBTypography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <AWBTypography fontSize="14px" color='text.dark' style={{ marginRight: 3 }} sx={{ alignSelf: "start", pt: 0.2 }}> {"SKU "} </AWBTypography>
                                <AWBTypography fontSize="17px" fontWeight="bold" color='text.dark'>
                                    {orderItem?.sku}
                                </AWBTypography>
                            </Box>
                        </Box>
                    </Grid>
                }
                {/** Copyright */}
                <Grid item xs={12}>
                    <Box sx={{ ...gridItemStyle, height: `${HEIGHT * 90 / 1168 - 2 * BORDER_WIDTH}px`, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                        <AWBTypography variant='subtitle2' color='text.dark'>Powered by</AWBTypography>
                        <Box component="img" sx={{ maxHeight: "20px", maxWidth: "80px", marginInline: "4px" }} src={require('src/_assets/mapit.png').default} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
};

export default AWB;
